'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';

const navItems = [
  { href: '/', label: 'Home' },
  { href: '/services', label: 'Services' },
  { href: '/service-areas', label: 'Service Areas' },
  { href: '/gallery', label: 'Gallery' },
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact', label: 'Contact' },
];

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  return (
    <header className="bg-white border-b border-gray-200 relative pb-5">
      {/* Top bar with phone number - visible on mobile/tablet */}
      <div className="bg-[color:var(--color-dark)] text-white py-2 text-center text-sm md:hidden">
        <a
          href="tel:3072773741"
          className="text-white font-semibold hover:text-yellow-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-dark)] active:opacity-90"
          aria-label="Call ES Plastering at (307) 277-3741"
        >
          📞 Call Now: (307) 277-3741
        </a>
      </div>
      <div className="container flex h-16 items-center justify-between px-4 relative">
        <Link href="/" className="flex items-center gap-3 text-[color:var(--color-dark)] absolute left-1/2 md:left-4 bottom-0 -translate-x-1/2 md:translate-x-0 z-50" style={{ transform: 'translateY(calc(50% + 10px))' }}>
          <Image src="/images/logo.png" alt="Erroll Scheurer Plastering Logo" width={160} height={42} priority className="drop-shadow-lg" style={{ height: 'auto' }} />
        </Link>
        <nav className="hidden md:flex items-center gap-2 lg:gap-4 lg:ml-48 md:ml-20">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="text-[color:var(--color-text-primary)] text-xs md:text-sm px-2 md:px-3 py-2 min-h-[44px] rounded-md whitespace-nowrap hover:text-[color:var(--color-primary-hover)] transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <button 
          className="md:hidden flex items-center justify-center rounded-md px-3 py-2 min-h-[44px] min-w-[44px] text-[color:var(--color-dark)]"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:3072773741"
            className="text-[color:var(--color-text-primary)] text-sm font-medium hover:text-[color:var(--color-primary)] flex items-center gap-1 transition-colors"
            aria-label="Call ES Plastering at (307) 277-3741"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            (307) 277-3741
          </a>
          <Link
            href="/contact"
            aria-label="Get Free Quote"
            className="inline-flex items-center justify-center rounded-md px-6 py-3 min-h-[48px] text-white font-medium"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            Get Free Quote
          </Link>
        </div>
      </div>
      {isMenuOpen && (
        <nav className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="container px-4 py-3">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="flex items-center px-4 py-3 rounded-lg text-base font-medium transition-all hover:bg-gray-50 hover:text-[color:var(--color-primary)] border-b border-gray-100 last:border-b-0"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="flex-1">{item.label}</span>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
              <a
                href="tel:3072773741"
                className="flex items-center justify-center w-full px-4 py-3 rounded-lg text-base font-medium border-2 border-[color:var(--color-primary)] text-[color:var(--color-primary)] transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                📞 Call (307) 277-3741
              </a>
              <Link
                href="/contact"
                className="flex items-center justify-center w-full px-4 py-3 rounded-lg text-base font-medium text-white transition-all"
                style={{ backgroundColor: 'var(--color-primary)' }}
                onClick={() => setIsMenuOpen(false)}
              >
                Get Free Quote
              </Link>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
